import { Button, Form, Input, Select, Typography, TreeSelect, message } from "antd";
import { t } from "i18next";
import { useAuth } from "@Hooks";
import { ITextMessage, ISharedCreateConversation } from "atlas-shared";
import { ContactSearchSelect } from "./ContactSearchSelect";
import { useFormOptions } from "../hooks";
import { useTextMessageSources } from "../hooks/useTextMessageSources";
import { countTotalOrganizations } from "../utils/organization";
import { useMemo, useState } from "react";
import { sendTextMessage } from "@Api/textMessageApi";
import { SendIcon } from "@Assets";

export const CreateTextMessageConversation = () => {
  const { Title } = Typography;
  const auth = useAuth();
  const [form] = Form.useForm<ITextMessage & { organization_id?: number; queueId?: string }>();
  const [messageLength, setMessageLength] = useState(0);
  const MAX_MESSAGE_LENGTH = 160;

  const initialValues = {
    organization_id: auth.user.__organizations[0]?.organization_id,
  };

  const { options: sourceSelectOptions, loading: sourcesLoading } = useTextMessageSources();

  const { organizationOptions, queueOptions } = useFormOptions({
    values: {
      ...form.getFieldsValue(),
      ...initialValues,
    } as Partial<ISharedCreateConversation>,
  });

  const handleOrganizationChange = (value: string) => {
    const newOrgId = Number(value);
    form.setFieldsValue({
      organization_id: newOrgId,
      queueId: undefined,
    });
  };

  const handleFinish = async (formValues: ITextMessage) => {
    if (!formValues.sourceId) return;

    try {
      const payload = {
        number: formValues.number,
        text: formValues.text,
        queueId: formValues.queueId,
      };

      await sendTextMessage(formValues.sourceId, payload);
      message.success(t("MESSAGE_SENT_SUCCESSFULLY"));
      form.resetFields();
      setMessageLength(0);
    } catch (error) {
      message.error(t("ERROR_SENDING_MESSAGE"));
      console.error("Error sending message:", error);
    }
  };

  const queueSelectOptions = useMemo(() => {
    return queueOptions.map((queue) => ({
      value: queue.id,
      label: queue.title,
    }));
  }, [queueOptions]);

  return (
    <main className="create-conversation-form">
      <Title level={2}>{t("CREATE_CONVERSATION")}</Title>
      <Form form={form} onFinish={handleFinish} initialValues={initialValues}>
        <div className="create-conversation-header">
          {countTotalOrganizations(organizationOptions) > 1 && (
            <div className="organization-select">
              <Form.Item name="organization_id" label={t("SELECT_ORGANIZATION")} required>
                <TreeSelect
                  className="organization"
                  treeData={organizationOptions}
                  treeDefaultExpandAll
                  placeholder={t("SELECT_ORGANIZATION")}
                  onChange={handleOrganizationChange}
                  fieldNames={{ label: "title", value: "key", children: "children" }}
                  showSearch
                  treeNodeFilterProp="title"
                />
              </Form.Item>
            </div>
          )}
          {form.getFieldValue("organization_id") && (
            <div className="queue-select">
              <Form.Item name="queueId" label={t("QUEUE")} required>
                <Select options={queueSelectOptions} placeholder={t("SELECT_QUEUE")} />
              </Form.Item>
            </div>
          )}
          <div className="source-select">
            <Form.Item name="sourceId" label={t("SOURCE")} required>
              <Select
                options={sourceSelectOptions}
                placeholder={t("SELECT_SOURCE")}
                loading={sourcesLoading}
              />
            </Form.Item>
          </div>
        </div>

        <div className="contact-section">
          <ContactSearchSelect
            organizationId={form.getFieldValue("organization_id")}
            agentprofileId={auth.user.__agentprofiles[0]}
            showContactNumber={true}
            required={true}
            onContactSelect={(_, __, contact_number) => {
              form.setFieldsValue({
                number: contact_number ?? "",
              });
            }}
          />
        </div>
        <Form.Item name="number" label={t("NUMBER")} hidden required />
        <Form.Item name="text" label={t("MESSAGE")} required>
          <Input.TextArea
            placeholder={t("MESSAGE")}
            rows={6}
            onChange={(e) => setMessageLength(e.target.value.length)}
            maxLength={MAX_MESSAGE_LENGTH}
          />
        </Form.Item>
        <div className="message-length-counter">
          <span>{messageLength}</span>
          <span>/{MAX_MESSAGE_LENGTH}</span>
        </div>
        <Button type="primary" htmlType="submit" className="send-button">
          <SendIcon /> {t("SEND")}
        </Button>
      </Form>
    </main>
  );
};
